import Heading from '@pelckmans/elementis/components/heading';
import Paragraph from '@pelckmans/elementis/components/paragraph';
import { Cell } from '@pelckmans/elementis/components/table';
import TextField from '@pelckmans/elementis/components/text-field';
import { useBreakpoint } from '@pelckmans/elementis/contexts/BreakpointsContext';
import { ComponentProps, useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportMembersContext } from '../contexts/ImportMembersContext';
import { linkColumnOptions, useImportMemberColumns } from '../hooks/useImportMemberColumns';
import EmptyTable from './EmptyTable';
import SelectFieldToLink from './SelectFieldToLink';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.IMPORT_MEMBERS.LINK_MEMBERS_DATA';

const getColumnSizeByOption = (option: string): ComponentProps<typeof Cell>['minWidth'] => {
  switch (option) {
    case linkColumnOptions.FIRST_NAME:
      return 'xs';
    case linkColumnOptions.USERNAME:
      return 'md';
    default:
      return 'sm';
  }
};

export default function LinkMembersData() {
  const { t } = useTranslation();
  const { linkOptions } = useImportMemberColumns();
  const id = useId();
  const breakpoint = useBreakpoint();

  const {
    fileMembersData,
    fileHeaderData,
    setColumnMapping,
    columnMapping,
    columnMappingByLinkedOption = {},
  } = useContext(ImportMembersContext);

  const handleOnChange = (LinkToColumnIndex: number, value: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [LinkToColumnIndex]: value === linkColumnOptions.DONT_LINK ? undefined : value,
    }));
  };

  const hasMappedColumns = Object.keys(columnMappingByLinkedOption).length > 0;

  const slicedData = fileMembersData?.slice(0, 8);

  return (
    <div className="link-members-data">
      <div className="modal-content-text">
        <Heading level={4} removeSpacing="top" className="modal-content-text-title" id={id}>
          {t(`${TRANSLATION_KEY}.TITLE`)}
        </Heading>
        <Paragraph size="md" removeSpacing className="modal-content-text-body">
          {t(`${TRANSLATION_KEY}.DESCRIPTION`)}
        </Paragraph>
      </div>
      <div className="modal-content-form">
        {fileHeaderData?.map((col, index) => {
          return (
            <div key={`fields-row-${index}`} className="fields-row-container">
              <TextField
                className="field"
                labelText={
                  index === 0
                    ? breakpoint !== 'mobile'
                      ? t(`${TRANSLATION_KEY}.FROM_FILE.LABEL`)
                      : t(`${TRANSLATION_KEY}.FROM_FILE.MOBILE_LABEL`)
                    : ''
                }
                disabled
                value={col}
                required
              />
              <SelectFieldToLink
                valuesByColumnIndex={columnMapping}
                onChange={handleOnChange}
                linkToColumnIndex={index}
              />
            </div>
          );
        })}
      </div>

      <div className="preview">
        <div className="subtexts">
          <Paragraph size="sm" removeSpacing className="modal-content-text-body">
            <b>{t(`${TRANSLATION_KEY}.PREVIEW`)}</b>
          </Paragraph>
          {hasMappedColumns && slicedData?.length === 8 && (
            <Paragraph size="sm" removeSpacing className="modal-content-text-body">
              {t(`${TRANSLATION_KEY}.FIRST_EIGHT_ROWS`)}
            </Paragraph>
          )}
        </div>
        {hasMappedColumns ? (
          <div className="table-container">
            <table className="table" aria-labelledby={id}>
              <thead>
                <tr>
                  {linkOptions.map(columnOption => {
                    const columnIndex = columnMappingByLinkedOption?.[columnOption];
                    if (!columnIndex) return null;

                    return (
                      <Cell.HeaderText
                        title={t(`${TRANSLATION_KEY}.TO_LINK.OPTIONS.${columnOption}`)}
                        key={`header-${columnIndex}-${columnOption}`}
                        minWidth={getColumnSizeByOption(columnOption)}
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {slicedData?.map((row, index) => (
                  <tr key={`row-${index}`}>
                    {linkOptions.map(columnOption => {
                      const columnIndex = columnMappingByLinkedOption?.[columnOption];
                      if (!columnIndex) return null;

                      return (
                        <Cell
                          density="compact"
                          key={`cell-${columnIndex}-${columnOption}-${index}`}
                          minWidth={getColumnSizeByOption(columnOption)}
                        >
                          <Cell.Text title={row[Number(columnIndex)]} />
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyTable id={id} />
        )}
      </div>
    </div>
  );
}
